import React, { FC } from 'react';

import cx from 'classnames';

import { Img } from 'components/base';
import { CircledNumber } from 'styled';

import sanityImgUtil from 'utils/sanityImgUtil';
import sanityImgSrcSetUtil from 'utils/sanityImgSrcSetUtil';
import withBreakpoints, {
  InjectedProps as WithBreakpointsProps,
} from 'lib/withBreakpoints';

import Language from 'constants/Language';
import {
  GeneralModuleColorScheme,
  GeneralModuleColor,
  SplitCtaColorScheme,
  EmphasizeModuleColorScheme,
} from 'constants/ColorScheme';

import { NumberedListWithImageModule as INumberedListWithImageModule } from 'types';

type Props = {
  module: INumberedListWithImageModule;
  hasWrapper?: boolean;
  wrapperColor?: GeneralModuleColor;
  applyArticleBodyWidthOffset?: boolean;
} & WithBreakpointsProps;

const NumberedListWithImageModule: FC<Props> = ({
  module,
  hasWrapper,
  wrapperColor,
  currentBreakpoint,
  applyArticleBodyWidthOffset,
}) => {
  const { moduleOptions, numberOptions, contents } = module;

  const colorScheme =
    GeneralModuleColorScheme[moduleOptions.color as GeneralModuleColor];
  const wrapperColorScheme = wrapperColor
    ? GeneralModuleColorScheme[wrapperColor]
    : null;

  const numberBgColor = SplitCtaColorScheme[numberOptions.bgColor].background;

  /**
   *  If there is wrapper component, use wrapper bg color as default.
   *  Otherwise, use module bg color as default.
   * */

  const numberColor = numberOptions.color
    ? EmphasizeModuleColorScheme[numberOptions.color].background
    : hasWrapper && wrapperColorScheme
    ? wrapperColorScheme.background
    : colorScheme.background;

  const breakpointIsMdDown = ['EXTRA_SMALL', 'SMALL', 'MEDIUM'].includes(
    currentBreakpoint
  );

  return (
    <div
      className={cx(
        'NumberedListWithImageModule module-content-padding-x py6 mxauto flex flex-col',
        {
          [`bg-color-${colorScheme.background}`]: !hasWrapper,
          'article-inner-text-full-width': applyArticleBodyWidthOffset,
        }
      )}
    >
      {module.topImage && (
        <Img
          alt={module.topImage.alt}
          style={{
            marginBottom: breakpointIsMdDown
              ? '0'
              : `${module.topImageBottomPosition}px`,
          }}
          className="NumberedListWithImageModule__top-image w100"
          sizes="(maxWidth: 768px) 768px, (maxWidth: 1024px) 1024px, 1990px"
          src={sanityImgUtil(module.topImage, 768)}
          srcSet={sanityImgSrcSetUtil(module.topImage, 768, 1024, 1990)}
          preloadContainerClassName="w100"
          dimensions={module.topImage.metadata?.dimensions}
          crop={module.topImage.crop}
        />
      )}
      <div
        className={cx('NumberedListWithImageModule__items flex flex-col', {
          'color-black': !hasWrapper && colorScheme.text === 'black',
          'color-white': !hasWrapper && colorScheme.text === 'white',
        })}
      >
        {contents.map((listItem, i, contents) => {
          const hasEvenIndex = i % 2 === 0;
          const listItemHasImage = !!listItem.image?.id;

          return (
            <div
              className={cx(
                'NumberedListWithImageModule__item flex flex-col md:flex-row md:items-center md:justify-between mb3_75 md:mb1_5',
                {
                  'flex-col-reverse': listItemHasImage && breakpointIsMdDown,
                  'items-center': listItemHasImage && breakpointIsMdDown,
                  'md:flex-row-reverse': !hasEvenIndex,
                }
              )}
              key={listItem.id}
            >
              <div className="NumberedListWithImageModule__item-text-container md:col-7 flex flex-row items-start mt3">
                <CircledNumber
                  mr={1.5}
                  circleColor={numberBgColor}
                  numberColor={numberColor}
                  number={i + 1}
                  ariaLabel={Language.t('Global.listOfPointsAriaLabel', {
                    currentNumber: i + 1,
                    totalNumber: contents.length,
                  })}
                  renderSmallerSizeOnMobile={true}
                />
                <div className="NumberedListWithImageModule__item-text flex-1">
                  <h2 className="text-md md-up_text-lg mb1_5">
                    {listItem.title}
                  </h2>
                  {!!listItem.description && (
                    <p className="text-secondary-sm">{listItem.description}</p>
                  )}
                </div>
              </div>
              {listItem.image && listItemHasImage && (
                <div className="NumberedListWithImageModule__item-image-container">
                  <Img
                    alt={listItem.image.alt}
                    className="w100"
                    src={sanityImgUtil(listItem.image, 400)}
                    preloadContainerClassName="w100"
                    dimensions={listItem.image.metadata?.dimensions}
                    crop={listItem.image.crop}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default withBreakpoints(NumberedListWithImageModule);
